import React from 'react';
import { TooltipProps } from 'recharts';
import { NameType, ValueType } from 'recharts/types/component/DefaultTooltipContent';
import { ChartTooltip, ChartTooltipProps } from '../ChartTooltip/ChartTooltip';
import { ChartTooltipInteraction } from '../ChartTooltip/ChartTooltipInteraction';
import { ChartTooltipItem, ChartTooltipItemProps } from '../ChartTooltip/ChartTooltipItem';
import { ChartClickInteraction } from '../types';

export type WidgetTooltipContentProps<V extends ValueType = ValueType, N extends NameType = NameType> = Omit<
  TooltipProps<V, N>,
  'content'
> & {
  onMouseEnter?: React.MouseEventHandler;
  onMouseLeave?: React.MouseEventHandler;
  onClick?: React.MouseEventHandler;
  interactions?: ChartClickInteraction[] | ((props?: WidgetTooltipContentProps<V, N>) => ChartClickInteraction[]);
  widgetData: unknown;
  title?: ChartTooltipItemProps['title'];
  titleDotColor?: ChartTooltipItemProps['dotColor'];
  titleIcon?: ChartTooltipItemProps['icon'];
  content?: ChartTooltipItemProps[];
  chartTooltipProps?: ChartTooltipProps;
  activeCellDataKey?: string;
};

export const WidgetTooltipContent = <TValue extends ValueType, TName extends NameType>(props: WidgetTooltipContentProps<TValue, TName>) => {
  const {
    payload,
    trigger,
    interactions = [],
    widgetData,
    onMouseLeave,
    onMouseEnter,
    onClick,
    title,
    titleDotColor,
    titleIcon,
    content = [],
    chartTooltipProps,
  } = props;
  const { dataKey, payload: cellPayload } = payload?.[0] || {};
  const isMenuOpen = trigger === 'click';
  const getFirstInteractionTitle = (text: string) => (isMenuOpen ? text : `Click to ${text}`);

  const availableInteractions =
    typeof interactions === 'function'
      ? interactions(props)
      : interactions
        ? interactions.filter(v => v.isEnabledForCell({ dataKey: payload?.[0]?.dataKey, ...payload?.[0]?.payload }))
        : interactions;

  const hasMultipleInteractions = availableInteractions.length > 1;

  return (
    <ChartTooltip
      role="tooltip"
      minWidth={221}
      maxWidth={221}
      boxShadow={t => t.shadows[10]}
      titleSlot={title || titleIcon || titleDotColor ? <ChartTooltipItem title={title} dotColor={titleDotColor} icon={titleIcon} /> : null}
      interactionsMenuOpen={isMenuOpen}
      onMouseLeave={onMouseLeave}
      onMouseEnter={onMouseEnter}
      onClick={onClick}
      interactions={
        hasMultipleInteractions && !isMenuOpen ? (
          <ChartTooltipInteraction primary="Click for interactions" />
        ) : (
          availableInteractions.map((interaction, index) => (
            <ChartTooltipInteraction
              key={interaction.title}
              primary={index === 0 ? getFirstInteractionTitle(interaction.title) : interaction.title}
              onClick={e => {
                e.stopPropagation();
                interaction.onClick({
                  d: cellPayload,
                  dataKey,
                  widgetData,
                  originalValue: cellPayload?.originalValue,
                  headCellType: cellPayload?.headCellType,
                });
              }}
            />
          ))
        )
      }
      {...chartTooltipProps}>
      {content.map((item, i) => (
        <ChartTooltipItem
          // eslint-disable-next-line react/no-array-index-key
          key={i}
          title={item.title}
          titleProps={item.titleProps}
          icon={item.icon}
          dotColor={item.dotColor}
          value={item.value}
          description={item.description}
        />
      ))}
    </ChartTooltip>
  );
};
